import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { Group, GroupService } from '../../../../providers/semantria-api/group.service';
import { AlertService } from '../../../../services/alert.service';

export interface GroupCreateDialogOptions {
  accountId: string;
  nameValidator: ValidatorFn;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-group-create-dialog',
  templateUrl: './group-create-dialog.component.html',
  styleUrls: ['./group-create-dialog.component.scss'],
  standalone: false,
})
export class GroupCreateDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: GroupCreateDialogOptions;
  @Output() modalClosed: EventEmitter<Group> = new EventEmitter<Group>();
  groupName: UntypedFormControl;

  isSaving: boolean;
  groupNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };

  constructor(
    private groupService: GroupService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.groupName = new UntypedFormControl(
      '',
      Validators.compose([Validators.required, this.data.nameValidator])
    );
  }

  onValidateGroupName() {
    if (this.groupName.hasError('required') || this.groupName.hasError('minLength')) {
      ErrorObjectUtils.setError(
        this.groupNameStatus,
        this.translocoService.translate('group-create-dialog.error.name-required')
      );
    } else if (this.groupName.hasError('nameInvalid')) {
      ErrorObjectUtils.setError(
        this.groupNameStatus,
        this.translocoService.translate('group-create-dialog.error.name-invalid')
      );
    } else if (this.groupName.hasError('nameAlreadyExists')) {
      ErrorObjectUtils.setError(
        this.groupNameStatus,
        this.translocoService.translate('group-create-dialog.error.name-already-exists')
      );
    } else if (this.groupName.hasError('maxLength')) {
      ErrorObjectUtils.setError(
        this.groupNameStatus,
        this.translocoService.translate('group-create-dialog.error.max-length', {
          maxLength: this.groupName.errors.maxLength.maxLength,
        })
      );
    } else {
      ErrorObjectUtils.setNoError(this.groupNameStatus);
    }
  }

  cancel() {
    this.modalClosed.emit(null);
  }

  create() {
    if (this.groupName.valid) {
      this.isSaving = true;
      this.groupName.disable();

      const group: Group = {
        group_id: undefined,
        group_name: this.groupName.value,
        account_id: this.data.accountId,
        members: [],
        permissions: [],
      };
      this.alertService.showToast(
        this.translocoService.translate('group-create-dialog.create-group', {
          groupName: group.group_name,
        })
      );

      this.groupService
        .createGroup(group)
        .then((res) => this.modalClosed.emit(res))
        .catch((err) => {
          this.isSaving = false;
          this.groupName.enable();

          this.alertService.showErrorToast(
            this.translocoService.translate('group-create-dialog.create-group-failed', {
              groupName: group.group_name,
            }),
            err
          );
        });
    }
  }
}
